import { Icon, Loader, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Button } from '@/components_deprecated/Button'
import { Table as BaseTable } from '@/components_deprecated/Table'
import type { EditUserEquipmentPromptResolve } from '@/modules/companies/CompanySettings/components/UserEquipmentSettings'
import { EditUserEquipmentPrompt } from '@/modules/companies/CompanySettings/components/UserEquipmentSettings'
import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import styled from 'styled-components'
import type { RelayNodeType } from '@/types/graphql'
import { useConfirm, useOnErrorAuto, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import type { AllUserEquipmentQuery, AllUserEquipmentQueryVariables } from '..'
import { ALL_USER_EQUIPMENT_QUERY } from '../queries'
import { ProfileCard } from '../UserProfile'
import { PermissionsRequired } from '@/containers/permission-containers'
import { useUserEquipmentMutations } from './mutations.hooks'
import { format } from '@/utils/date-fns'

const Wrapper = styled(ProfileCard).attrs<{ area: string }>({
  area: 'equipment',
})`
  display: flex;
  flex-direction: column;

  header {
    display: flex;

    h3 {
      display: flex;

      div.loader {
        margin-left: 0.5rem;
      }
    }
  }
  footer {
    display: flex;
    justify-content: flex-end;
  }
`

const Table = styled(BaseTable)`
  flex-grow: 1;
`

export type UserEquipment = Exclude<
  RelayNodeType<AllUserEquipmentQuery['allUserEquipment']>,
  undefined
>

interface UserEquipmentTableProps {
  userId: string
}

export const UserEquipmentTable: React.FC<UserEquipmentTableProps> = ({
  userId,
}) => {
  const translations = useTranslate({
    userEquipment: 'common.user-equipment',
    notPermitted: 'error.not-permitted',
    add: 'common.add',
    equipmentType: 'equipment.equipment-type',

    name: 'common.name',
    assignedDate: 'equipment.assigned-date',
    returnDate: 'equipment.return-date',

    noEquipment: 'equipment.no-equipment',
    none: 'common.none',
    close: 'common.close',
    finished: 'common.finished',

    prompt: {
      delete: ['settings.prompts.user-equipment-delete', { name: '' }],
      deleteTitle: 'settings.prompts.user-equipment-delete-title',
    },
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()
  const onErrorAuto = useOnErrorAuto()
  const mutations = useUserEquipmentMutations()

  const { hasPermissions } = usePermissions()
  const canEdit = hasPermissions(PERMISSIONS.users.change.userequipment)
  const canDelete = hasPermissions(PERMISSIONS.users.delete.userequipment)

  const {
    data: equipmentData,
    loading: equipmentLoading,
    error: equipmentError,
  } = useQuery<AllUserEquipmentQuery, AllUserEquipmentQueryVariables>(
    ALL_USER_EQUIPMENT_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables: {
        user: userId,
        orderBy: 'assignedDate',
      },
      onError: onErrorAuto(),
    }
  )

  async function handleCreate() {
    const { data } = await addPrompt<EditUserEquipmentPromptResolve | null>(
      resolve => <EditUserEquipmentPrompt onSubmit={resolve} />
    )
    if (data === null) return
    const { form } = data

    void mutations.create({
      variables: {
        input: {
          user: userId,
          name: form.name,
          equipmentType: form.equipmentType,
          assignedDate: form.assignedDate,
          returnDate: form.useReturnDate ? form.returnDate : null,
        },
      },
    })
  }

  async function handleEdit(equipment: UserEquipment) {
    const { data } = await addPrompt<EditUserEquipmentPromptResolve | null>(
      resolve => (
        <EditUserEquipmentPrompt userEquipment={equipment} onSubmit={resolve} />
      )
    )
    if (data === null) return
    const { form } = data

    void mutations.patch({
      variables: {
        id: equipment.id,
        input: {
          name: form.name,
          equipmentType: form.equipmentType,
          assignedDate: form.assignedDate,
          returnDate: form.useReturnDate ? form.returnDate : null,
        },
      },
    })
  }

  async function handleDelete(equipment: UserEquipment) {
    const { data: answer } = await confirm(
      translations.prompt.delete({ name: equipment.name }),
      translations.prompt.deleteTitle
    )
    if (!answer) return

    void mutations.delete({
      variables: { id: equipment.id },
    })
  }

  const equipment = useMemo(
    () => equipmentData?.allUserEquipment.edges.map(edge => edge.node) ?? [],
    [equipmentData]
  )

  const isLoading = equipmentLoading || mutations.loading
  return (
    <Wrapper area="user-equipment">
      <header>
        <h3>
          {translations.userEquipment}
          {isLoading && (
            <div className="loader">
              <Loader.Spinner size={28} />
            </div>
          )}
        </h3>
      </header>

      <Table
        noShadow
        noBorders
        noHeaderMargin
        noRowMargin
        noData={!equipment.length}
        noDataText={
          !equipmentLoading && !!equipmentError
            ? translations.notPermitted
            : translations.noEquipment
        }
        noDataColor={!equipmentLoading && !!equipmentError ? 'red' : undefined}
      >
        <thead>
          <tr>
            <th>{translations.name}</th>
            <th>{translations.equipmentType}</th>
            <th>{translations.assignedDate}</th>
            <th>{translations.returnDate}</th>
            {canEdit && <th></th>}
            {canDelete && <th></th>}
          </tr>
        </thead>

        <tbody>
          {equipment.map(equipment => {
            const assignedDate = equipment.assignedDate
              ? format(new Date(equipment.assignedDate), 'PP')
              : '-'
            const returnDate = equipment.returnDate
              ? format(new Date(equipment.returnDate), 'PP')
              : '-'

            const equipmentTypeLabel = equipment.equipmentType?.name ?? '-'

            return (
              <tr key={equipment.id}>
                <td>{equipment.name}</td>
                <td>{equipmentTypeLabel}</td>
                <td className="center" width="1px">
                  {assignedDate}
                </td>
                <td className="center" width="1px">
                  {returnDate}
                </td>

                {canEdit && (
                  <td width="1px">
                    <Icon
                      icon="edit"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="secondary"
                      onClick={() => handleEdit(equipment)}
                    />
                  </td>
                )}

                {canDelete && (
                  <td width="1px">
                    <Icon
                      icon="times"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="red"
                      onClick={() => handleDelete(equipment)}
                    />
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </Table>

      <footer>
        <PermissionsRequired permissions={PERMISSIONS.users.add.userequipment}>
          <Button
            color="white"
            height="3rem"
            onClick={handleCreate}
            disabled={mutations.loading}
          >
            {translations.add}
          </Button>
        </PermissionsRequired>
      </footer>
    </Wrapper>
  )
}
